import checkIfBlockIsVisible from "./checkIfBlockIsVisible";
import checkIfStepIsVisible from "./checkIfStepIsVisible";
import _ from "lodash";
import getExpressionConfiguration from "./getExpressionConfiguration";

// TODO remove these types once the services module will be migrated to TS
type ContextResponses = {
  [questionId: number]: {
    question: {
      step_id: number | null,
      dependencies?: string[],
      [key: string]: any,
    },
    configuration_input_id?: number,
    [key: string]: any,  // other fields
  }
}
type ContextStep = {
  id: number,
  dependencies?: string[],
  [key: string]: any,  // other fields
}
type ContextConfiguration = {
  id: number,
  [key: string]: any,  // other fields
}
type ContextType = {
  responses: ContextResponses,
  data: ContextStep[],
  configuration: ContextConfiguration[],
  [key: string]: any,  // other fields
}

/**
 * Checks if the provided question appears among the provided dependencies array
 * @returns boolean true if the question is in the dependencies array, false otherwise
 * @param qid question id to search for
 * @param dependencies dependencies array to be checked
 * */
const isQuestionInDependencies = (qid: number, dependencies?: string[]) => {
  if (!dependencies?.length)
      return false;
  return dependencies
    .filter((d) => d?.[0] === '{')
    .map((d) => JSON.parse(d))
    .some(({inputId}) => inputId === qid);
}

/**
 * Checks if the response associated with the target object should be removed
 * @param qid id of the question of whose response has been changed
 * @param target the target object (either a step or a question block)
 * @param context the context object containing the responses, the steps and the blocks
 * @param isVisible the callback function to be called to check if the target object is visible
 */
const shouldRemoveResponse = (
  qid: number,
  target: any,
  context: ContextType,  // TODO remove this type once the services module will be migrated to TS
  isVisible: (target: any, context: ContextType) => boolean,  // TODO remove this type once the services module will be migrated to TS
) => {
  if (!target)
    return false;
    /*
  before the check was only:
  return isQuestionInDependencies(qid, target.dependencies) && !isVisible(target, context);

  hubspot ticket 35072171326
  there was a case in which you had:
  -Q1 a select with 2 options A and B
  -Q2 a K-Color question (product-color-package)
  -Q0 a select with  many option C,D,E,F,G
  A had a dependency on the response of Q0, and also B had a dependency on the response of Q0
  Q2 had the following dependency: Q1 == A
  Q1 had not dependencies on Q0 directly, only its options had dependencies on Q0!!!
  when you changed answer in Q0 and selected an option that made A invisible but B still visible and moved
  to the next step where Q1 and Q2 were located, Q2 was no longer visible but its answer was still present in
  the responses because:
  -Q0 did not appear directly in the dependencies of Q2 neither in the dependencies of Q1
   */
  // Helper: returns true if:
  // 1. the changed question id (qid) appears in the evaluated dependencies of the entity
  // 2. the entity is now not visible
  // This covers both dependencies and cross-configurations-dependencies via getExpressionConfiguration.
  const isInDependenciesAndIsNotVisible = (entity: any): boolean => {
    if (!entity) return false;
    const deps = getExpressionConfiguration(entity, context, "dependencies");
    return isQuestionInDependencies(qid, deps) && !isVisible(entity, context);
  };

  // Base invalidation: the target itself depends on qid and is now hidden.
  const targetInvalid = isInDependenciesAndIsNotVisible(target);

  switch (target.input_type) {
    // Single select: remove response if the selected option became hidden due to qid change
    case "select": {
      const selectedOption = context.responses[target.id]?.option;
      const optionInvalid = isInDependenciesAndIsNotVisible(selectedOption);
      return targetInvalid || optionInvalid;
    }
    // Multi select: if any selected option is now hidden because of qid change, remove entire response
    case "multi_select": {
      const selectedOptionIds: number[] =
        (context.responses[target.id]?.value || []).map(Number);

      const selectedOptions = context.configuration.filter(
        (opt: any) => selectedOptionIds.includes(opt.id)
      );

      const anyOptionInvalid = selectedOptions.some(isInDependenciesAndIsNotVisible);
      return targetInvalid || anyOptionInvalid;
    }
    // Other: only the target block visibility/dependency matters
    default:
      return targetInvalid;
  }
}

/**
 * Returns the list of responses that should be reset when the provided question is changed
 * @returns number[] the ids of the responses that should be reset
 * @param qid id of the question of whose response has been changed
 * @param context the context object containing the responses, the steps and the blocks
 * */
export const getSurveyResponsesToRemove = (
  qid: number | undefined,
  context: ContextType,  // TODO remove this type once the services module will be migrated to TS
): number[] => {

  const {
    responses,
    data: steps,
    configuration: blocks,
  } = context;

  // if the question id is not provided return an empty array
  if (!qid)
    return [];

  // iterate all current responses and remove the ones that were activated by the old response (NOTE: if a dependent
  // question has the current question inside its dependencies but it's still visible, we should not delete its
  // response)
  const result = Object.values(responses)
    .filter(({
      question: {
        id: questionId,
        step_id: stepId,
        dependencies: questionDependencies
      }
    }) => {
      // if the current question appears among the dependencies of the current response's step and/or block and at least
      // one of them is not visible anymore, the response should be removed
      const step = steps.find(({id}) => id === stepId);
      const block = blocks.find(({id}) => id === questionId);
      return shouldRemoveResponse(qid, step, context, checkIfStepIsVisible)
        || shouldRemoveResponse(qid, block, context, checkIfBlockIsVisible);
    })
    .reduce(
      // add each resulting response to the set of responses to be removed; also, add the possible responses that
      // depend on each dependent response (and so on, recursively)
      (acc, {configuration_input_id: dependentQuestionId}) => {
        if (!dependentQuestionId)
          return acc;

        const dependentQuestionDependentResponses=
          getSurveyResponsesToRemove(
            dependentQuestionId,
            {
              ...context,
              responses: _.omit(responses, [dependentQuestionId]),
            }
          );

        return new Set([
          ...Array.from(acc),  // already added ids
          dependentQuestionId,  // current response id
          ...dependentQuestionDependentResponses,  // ids of the possible responses that depend on the current response
        ]);
      },
      new Set<number>(),
    );

  return Array.from(result);
}